import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
    CheckCircle2, Upload, Camera, Loader2, Package, AlertTriangle,
    Banknote, Zap, FileSignature,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAIEMENTS = [
    { value: 'especes', label: 'Espèces', icon: Banknote },
    { value: 'virement', label: 'Virement', icon: Zap },
    { value: 'cheque_ao', label: 'Chèque AO', icon: FileSignature },
    { value: 'cheque_so', label: 'Chèque SO', icon: FileSignature },
];

const todayISO = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const j = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${j}`;
};

export default function LivraisonForm() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const [form, setForm] = useState({
        nom: '',
        livreur: '',
        date: todayISO(),
        montant: '',
        paiement_type: '',
        problemes: 'non',
        description: '',
        files: [],
    });

    useEffect(() => {
        (async () => {
            try {
                await axios.get(`${API_URL}/api/public/livraison-form/${token}`);
            } catch (e) {
                setError(e?.response?.data?.detail || 'Lien invalide ou expiré');
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const onFile = (e) => {
        const picked = Array.from(e.target.files || []);
        if (!picked.length) return;
        const readers = picked.map(f => new Promise((resolve) => {
            if (f.size > 8 * 1024 * 1024) {
                toast.error(`${f.name} — trop volumineux (max 8 Mo)`);
                return resolve(null);
            }
            const reader = new FileReader();
            reader.onload = () => resolve({ file_data: reader.result, file_name: f.name });
            reader.readAsDataURL(f);
        }));
        Promise.all(readers).then(results => {
            const valid = results.filter(Boolean);
            setForm(p => ({ ...p, files: [...p.files, ...valid] }));
        });
        e.target.value = '';
    };

    const removeFile = (idx) => {
        setForm(p => ({ ...p, files: p.files.filter((_, i) => i !== idx) }));
    };

    const submit = async () => {
        if (!form.nom.trim()) { toast.error('Nom obligatoire'); return; }
        if (!form.livreur.trim()) { toast.error('Livreur obligatoire'); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/public/livraison-form/${token}`, form);
            setSubmitted(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    }
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
                <div className="max-w-sm w-full bg-white rounded-2xl border border-red-200 shadow-sm p-6 text-center" data-testid="livraison-error">
                    <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <h1 className="text-lg font-bold text-red-600 mb-1">Lien indisponible</h1>
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            </div>
        );
    }
    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-emerald-50">
                <div className="max-w-sm w-full bg-white rounded-2xl border border-emerald-200 shadow-sm p-6 text-center" data-testid="livraison-success">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <h1 className="text-lg font-bold text-[#1B365D] mb-1">Livraison enregistrée</h1>
                    <p className="text-sm text-gray-500">Merci ! Les informations ont bien été transmises.</p>
                    <button
                        onClick={() => {
                            setSubmitted(false);
                            setForm({
                                nom: '', livreur: '', date: todayISO(), montant: '',
                                paiement_type: '', problemes: 'non', description: '', files: [],
                            });
                        }}
                        className="mt-5 w-full py-2.5 rounded-xl bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#152a4a] transition-colors"
                        data-testid="livraison-new"
                    >
                        Nouvelle livraison
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20" data-testid="livraison-form">
            <Toaster richColors />
            {/* Header */}
            <div className="bg-[#1B365D] text-white px-5 py-6">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#C9A962]/20 flex items-center justify-center">
                        <Package className="w-5 h-5 text-[#C9A962]" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold">Rapport de livraison</h1>
                        <p className="text-white/60 text-xs mt-0.5">Luxy Move · Espace livreurs</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="px-4 mt-4 space-y-4">
                <Field label="Nom" required>
                    <input
                        className="form-inp"
                        placeholder="Nom du client / de la livraison"
                        value={form.nom}
                        onChange={e => setForm({ ...form, nom: e.target.value })}
                        data-testid="liv-nom"
                    />
                </Field>

                <Field label="Livreur" required>
                    <input
                        className="form-inp"
                        placeholder="Nom du livreur"
                        value={form.livreur}
                        onChange={e => setForm({ ...form, livreur: e.target.value })}
                        data-testid="liv-livreur"
                    />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Date">
                        <input
                            type="date"
                            className="form-inp"
                            value={form.date}
                            onChange={e => setForm({ ...form, date: e.target.value })}
                            data-testid="liv-date"
                        />
                    </Field>
                    <Field label="Montant (EUR)">
                        <input
                            type="number"
                            step="0.01"
                            className="form-inp"
                            placeholder="0.00"
                            value={form.montant}
                            onChange={e => setForm({ ...form, montant: e.target.value })}
                            data-testid="liv-montant"
                        />
                    </Field>
                </div>

                <Field label="Mode de paiement">
                    <div className="grid grid-cols-2 gap-2">
                        {PAIEMENTS.map(m => {
                            const Icon = m.icon;
                            const active = form.paiement_type === m.value;
                            return (
                                <button key={m.value} type="button"
                                    onClick={() => setForm({ ...form, paiement_type: m.value })}
                                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                        active ? 'bg-[#1B365D] text-white border-[#1B365D]'
                                               : 'bg-white text-gray-600 border-gray-200'
                                    }`}
                                    data-testid={`liv-pay-${m.value}`}
                                >
                                    <Icon className="w-4 h-4" /> {m.label}
                                </button>
                            );
                        })}
                    </div>
                </Field>

                <Field label="Problème ?">
                    <div className="flex gap-2">
                        {['non', 'oui'].map(v => (
                            <button key={v} type="button" onClick={() => setForm({ ...form, problemes: v })}
                                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                    form.problemes === v
                                        ? (v === 'oui' ? 'bg-red-500 text-white border-red-500' : 'bg-emerald-500 text-white border-emerald-500')
                                        : 'bg-white text-gray-600 border-gray-200'
                                }`}
                                data-testid={`liv-probleme-${v}`}
                            >{v === 'oui' ? 'OUI' : 'NON'}</button>
                        ))}
                    </div>
                </Field>

                {form.problemes === 'oui' && (
                    <Field label="Description du problème">
                        <textarea rows={3} className="form-inp"
                            placeholder="Expliquez en quelques mots..."
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            data-testid="liv-desc"
                        />
                    </Field>
                )}

                <Field label="Photos / fichiers (plusieurs possibles)">
                    <div className="space-y-2">
                        <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-5 cursor-pointer hover:border-[#C9A962] transition-colors">
                            <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                                onChange={onFile} data-testid="liv-file" />
                            <Camera className="w-6 h-6 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">Ajouter photo(s) ou fichier(s)</span>
                            <span className="text-[10px] text-gray-400 mt-0.5">Max 8 Mo par fichier</span>
                        </label>
                        {form.files.length > 0 && (
                            <ul className="space-y-1" data-testid="liv-files-list">
                                {form.files.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                        <span className="flex-1 min-w-0 truncate text-[#1B365D] font-medium">{f.file_name}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(i)}
                                            className="text-xs text-red-500 hover:text-red-700 px-1"
                                            data-testid={`liv-file-remove-${i}`}
                                            aria-label="Supprimer"
                                        >
                                            ✕
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Field>

                <button onClick={submit} disabled={submitting}
                    className="w-full py-3 mt-2 rounded-xl bg-[#C9A962] text-white font-semibold text-sm shadow-sm hover:bg-[#b5924e] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="liv-submit"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Envoyer le rapport
                </button>
            </div>

            <style>{`.form-inp{width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:10px;font-size:14px;background:white;}.form-inp:focus{outline:none;border-color:#1B365D;box-shadow:0 0 0 3px rgba(27,54,93,0.1);}`}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {children}
        </div>
    );
}
